import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../../contexts/CartContext';
import { AuthContext } from '../../contexts/AuthContext';
import './User.css';

const DELIVERY_CHARGES = 120;

export default function Checkout() {
  const { cart, getTotal, clearCart } = useContext(CartContext);
  const { api, setOrdersBadge } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    deliveryAddress: '',
    deliveryPhone: '',
    paymentMethod: 'cash'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const subtotal = getTotal();
  const grandTotal = subtotal + DELIVERY_CHARGES;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.deliveryAddress || !formData.deliveryPhone) {
      setError('Please fill all delivery details');
      return;
    }

    if (formData.deliveryPhone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    if (cart.length === 0) {
      setError('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        items: cart.map(item => ({
          productId: item.id,
          quantity: item.quantity,
          price: item.price,
          name: item.name
        })),
        totalAmount: grandTotal,
        deliveryCharges: DELIVERY_CHARGES,
        deliveryAddress: formData.deliveryAddress,
        deliveryPhone: formData.deliveryPhone,
        paymentMethod: formData.paymentMethod
      };

      const response = await api.post('/orders', orderData);

      const orderNumber = response.data?.orderNumber || response.data?.order_number;

      // Set orders badge
      if (typeof setOrdersBadge === 'function') {
        setOrdersBadge(1);
      }

      clearCart();

      // Show success and redirect
      alert(
        orderNumber
          ? `Order ${orderNumber} placed successfully! 🎉`
          : 'Order placed successfully! 🎉'
      );

      navigate('/user/orders');
    } catch (err) {
      console.error('Order error:', err);
      setError(err.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="user-checkout">
        <div className="container">
          <div className="empty-state">
            <div className="empty-state-icon">🛒</div>
            <h2 className="empty-state-title">Your cart is empty</h2>
            <p className="empty-state-text">Add items to cart before checkout</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/user')}
            >
              Start Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-checkout">
      <div className="container">
        <h1 className="page-title">
          <span className="title-icon">💳</span>
          Checkout
        </h1>

        <div className="checkout-content">
          <div className="checkout-form-section">
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="alert alert-error">
                  <span className="alert-icon">⚠️</span>
                  <span className="alert-text">{error}</span>
                </div>
              )}

              {/* Order Summary */}
              <div className="card">
                <h3 className="card-title">📦 Order Summary</h3>
                <div className="order-items">
                  {cart.map((item) => (
                    <div key={item.id} className="order-item-row">
                      <span className="item-name">
                        {item.name} x {item.quantity}
                      </span>
                      <span className="item-price">
                        Rs. {item.price * item.quantity}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="summary-divider"></div>

                <div className="order-item-row">
                  <span className="item-name">Subtotal</span>
                  <span className="item-price">Rs. {subtotal}</span>
                </div>

                <div className="order-item-row">
                  <span className="item-name">Delivery Charges</span>
                  <span className="item-price">Rs. {DELIVERY_CHARGES}</span>
                </div>

                <div className="summary-divider"></div>

                <div className="order-item-row total-row">
                  <span className="item-name">Grand Total</span>
                  <span className="item-price grand-total">Rs. {grandTotal}</span>
                </div>
              </div>

              {/* Delivery Details */}
              <div className="card">
                <h3 className="card-title">📍 Delivery Details</h3>

                <div className="form-group">
                  <label htmlFor="deliveryAddress">Delivery Address *</label>
                  <textarea
                    id="deliveryAddress"
                    name="deliveryAddress"
                    value={formData.deliveryAddress}
                    onChange={handleChange}
                    placeholder="Enter complete delivery address"
                    rows="3"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="deliveryPhone">Phone Number *</label>
                  <input
                    id="deliveryPhone"
                    name="deliveryPhone"
                    type="tel"
                    value={formData.deliveryPhone}
                    onChange={handleChange}
                    placeholder="03XX-XXXXXXX"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Payment Method */}
              <div className="card">
                <h3 className="card-title">💳 Payment Method</h3>

                <div className="payment-options">
                  <label className={`payment-option ${formData.paymentMethod === 'cash' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={formData.paymentMethod === 'cash'}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <div className="payment-content">
                      <span className="payment-icon">💵</span>
                      <div className="payment-info">
                        <div className="payment-title">Cash on Delivery</div>
                        <div className="payment-subtitle">Pay when you receive</div>
                      </div>
                    </div>
                    {formData.paymentMethod === 'cash' && (
                      <span className="payment-check">✓</span>
                    )}
                  </label>

                  <label className={`payment-option ${formData.paymentMethod === 'card' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={formData.paymentMethod === 'card'}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <div className="payment-content">
                      <span className="payment-icon">💳</span>
                      <div className="payment-info">
                        <div className="payment-title">Card Payment</div>
                        <div className="payment-subtitle">Coming soon</div>
                      </div>
                    </div>
                    {formData.paymentMethod === 'card' && (
                      <span className="payment-check">✓</span>
                    )}
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className={`btn btn-primary btn-block ${loading ? 'btn-loading' : ''}`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-small"></span>
                      Placing Order...
                    </>
                  ) : (
                    `Place Order - Rs. ${grandTotal}`
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-outline btn-block"
                  onClick={() => navigate('/user/cart')}
                  disabled={loading}
                >
                  Back to Cart
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}